import { useEffect, useState } from 'react';

// Singleton state to track Google Maps loading across all components
let globalIsLoaded = false;
let globalIsLoading = false;
let globalError: string | null = null;
let loadPromise: Promise<void> | null = null;

export const useGoogleMaps = (shouldLoad: boolean = true) => {
  const [isLoaded, setIsLoaded] = useState(globalIsLoaded);
  const [error, setError] = useState<string | null>(globalError);
  const [isLoading, setIsLoading] = useState(globalIsLoading);

  useEffect(() => {
    if (!shouldLoad) {
      return;
    }

    // If already loaded, update state immediately
    if (globalIsLoaded) {
      setIsLoaded(true);
      setIsLoading(false);
      return;
    }

    // If there's an error, update state immediately
    if (globalError) {
      setError(globalError);
      setIsLoading(false);
      return;
    }

    // Check if Google Maps is already loaded
    if (window.google && window.google.maps) {
      globalIsLoaded = true;
      setIsLoaded(true);
      setIsLoading(false);
      return;
    }

    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      globalError = 'Google Maps API key is missing';
      setError(globalError);
      setIsLoading(false);
      return;
    }

    // If already loading, wait for the existing promise
    if (loadPromise) {
      setIsLoading(true);
      loadPromise
        .then(() => {
          setIsLoaded(globalIsLoaded);
          setError(globalError);
          setIsLoading(false);
        })
        .catch(() => {
          setError(globalError);
          setIsLoading(false);
        });
      return;
    }

    // Start loading
    globalIsLoading = true;
    setIsLoading(true);

    // Create loading promise
    loadPromise = new Promise<void>((resolve, reject) => {
      const scriptId = 'google-maps-script';
      let script = document.getElementById(scriptId) as HTMLScriptElement | null;

      // If script exists, it might still be loading
      if (script) {
        if (window.google && window.google.maps) {
          globalIsLoaded = true;
          globalIsLoading = false;
          resolve();
          return;
        }

        // Wait for the existing script to load
        const checkInterval = setInterval(() => {
          if (window.google && window.google.maps) {
            clearInterval(checkInterval);
            globalIsLoaded = true;
            globalIsLoading = false;
            resolve();
          }
        }, 100);

        setTimeout(() => {
          clearInterval(checkInterval);
          if (!globalIsLoaded) {
            globalError = 'Google Maps loading timeout';
            globalIsLoading = false;
            reject(new Error(globalError));
          }
        }, 10000);
        return;
      }

      // Create new script with loading=async parameter
      script = document.createElement('script');
      script.id = scriptId;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        globalIsLoaded = true;
        globalIsLoading = false;
        resolve();
      };

      script.onerror = () => {
        globalError = 'Failed to load Google Maps API';
        globalIsLoading = false;
        console.error('Google Maps API failed to load');
        reject(new Error(globalError));
      };

      document.head.appendChild(script);
    });

    // Wait for the promise
    loadPromise
      .then(() => {
        setIsLoaded(true);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Error loading Google Maps:', err);
        setError(globalError);
        setIsLoading(false);
      });
  }, [shouldLoad]);

  return { isLoaded, error, isLoading };
};
